'use client';

import { AnalyticsEvent } from '@provider-directory/types/analytics.contracts';
import { getClientProperties } from './analytics-device-properties';

/**
 * Internal Analytics Plugin
 *
 * Intercepts all analytics events (same payload Mixpanel receives) and forwards
 * them to the PDS analytics API for storage in MongoDB.
 *
 * Design:
 * - Batched: buffers events and flushes every 30s or when 10 events accumulate
 * - Reliable: uses sendBeacon on page unload so no events are lost
 * - Non-blocking: failures are silent — analytics should never break the UX
 */

// Maps frontend property names → PDS DTO field names
const PROPERTY_MAP: Record<string, string> = {
  pNPI: 'providerNpi',
  pName: 'providerName',
  pSpecialty: 'providerSpecialty',
  pGender: 'providerGender',
  pHospitalGroup: 'providerHospitalAssociation',
  pLanguage: 'providerLanguage',
  pLocation: 'providerLocation',
  pSchedulingType: 'providerSchedulingType',
  doctorID: 'doctorId',
  nameSelect: 'nameSelect',
  assocDepartment: 'assocDepartment',
  fSpecialty: 'searchSpecialty',
  fLocation: 'searchLocation',
  conversionType: 'conversionType',
  clickText: 'clickText',
  clickURL: 'clickUrl',
  clickUrl: 'clickUrl',
  componentType: 'componentType',
  linkContextPage: 'linkContextPage',
  linkContextComponent: 'linkContextComponent',
  specialtyBranch: 'specialtyBranch',
  questionId: 'questionId',
  answer: 'answerId',
  reasonForVisit: 'reasonForVisit',
  visitTypeId: 'visitTypeId',
  schedulingFlowType: 'schedulingFlowType',
  appType: 'appType',
  previousEvent: 'previousEvent',
};

const KNOWN_DTO_FIELDS = new Set([
  ...Object.values(PROPERTY_MAP),
  'organizationCode',
]);

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 30_000;

export interface InternalAnalyticsPluginConfig {
  endpoint?: string;
  batchSize?: number;
  flushIntervalMs?: number;
}

/**
 * Property values produced by the analytics library. Properties may be
 * primitives, arrays of primitives, or nested objects (rare). Using `unknown`
 * forces consumers to narrow before reading.
 */
type PropertyValue = string | number | boolean | null | undefined | unknown[];
type EventProperties = Record<string, PropertyValue>;

/**
 * Shape of the payload handed to the plugin's `page` and `track` hooks by
 * the `analytics` library. We only consume what we need.
 */
interface PagePayload {
  properties?: EventProperties & { path?: string; pageName?: string };
}
interface TrackPayload {
  event: string;
  properties?: EventProperties;
}

interface InternalAnalyticsPlugin {
  name: string;
  config: { endpoint: string };
  initialize: () => void;
  page: (input: { payload: PagePayload }) => void;
  track: (input: { payload: TrackPayload }) => void;
  loaded: () => boolean;
}

const SESSION_ID_KEY = 'pd.analytics.internal.sessionId';

function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

function mapProperties(
  eventName: string,
  properties: EventProperties,
): AnalyticsEvent {
  const sessionId = getOrCreateSessionId();
  const clientProps = getClientProperties();
  const mapped: Record<string, PropertyValue> = {};
  const extra: Record<string, PropertyValue> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null || value === '') continue;

    const dtoKey = PROPERTY_MAP[key];
    if (dtoKey) {
      mapped[dtoKey] = value;
    } else if (KNOWN_DTO_FIELDS.has(key)) {
      mapped[key] = value;
    } else {
      extra[key] = value;
    }
  }

  const deviceFields: Record<string, unknown> = {
    userAgent: clientProps.userAgent,
    screenHeight: clientProps.screenHeight,
    screenWidth: clientProps.screenWidth,
    referrer: clientProps.referrer,
    referringDomain: clientProps.referringDomain,
    language: clientProps.language,
  };

  const { utm } = clientProps;
  if (utm.utmSource) deviceFields.utmSource = utm.utmSource;
  if (utm.utmMedium) deviceFields.utmMedium = utm.utmMedium;
  if (utm.utmCampaign) deviceFields.utmCampaign = utm.utmCampaign;
  if (utm.utmContent) deviceFields.utmContent = utm.utmContent;
  if (utm.utmTerm) deviceFields.utmTerm = utm.utmTerm;

  return {
    eventName,
    sessionId,
    timestamp: new Date().toISOString(),
    pageUrl: clientProps.currentUrl,
    ...deviceFields,
    ...mapped,
    ...(Object.keys(extra).length > 0 ? { extraFields: extra } : {}),
  };
}

/**
 * Event buffer that flushes on interval, batch size, or page unload.
 */
function createEventBuffer(
  endpoint: string,
  batchSize: number,
  flushIntervalMs: number,
) {
  let buffer: AnalyticsEvent[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;

  const flush = () => {
    if (buffer.length === 0) return;
    const events = buffer;
    buffer = [];

    const body = JSON.stringify({ events });

    // Use sendBeacon if available (works during page unload), otherwise fetch
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(
        endpoint,
        new Blob([body], { type: 'application/json' }),
      );
    } else {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        // eslint-disable-next-line promise/prefer-await-to-then
      }).catch((err: unknown) => {
        // ignore errors — analytics should never break the UX
        console.warn('[internal-analytics] batch send failed', err);
      });
    }
  };

  const enqueue = (event: AnalyticsEvent) => {
    buffer.push(event);
    if (buffer.length >= batchSize) {
      flush();
    }
  };

  const start = () => {
    timer = setInterval(flush, flushIntervalMs);

    // Flush remaining events when the page is being unloaded
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    window.addEventListener('pagehide', flush);
  };

  const stop = () => {
    if (timer) clearInterval(timer);
    flush();
  };

  return { enqueue, start, stop, flush };
}

export default function internalAnalyticsPlugin(
  pluginConfig: InternalAnalyticsPluginConfig = {},
): InternalAnalyticsPlugin {
  const endpoint = pluginConfig.endpoint ?? '/pds-api/analytics/events';
  const batchSize = pluginConfig.batchSize ?? BATCH_SIZE;
  const flushIntervalMs = pluginConfig.flushIntervalMs ?? FLUSH_INTERVAL_MS;

  const eventBuffer = createEventBuffer(endpoint, batchSize, flushIntervalMs);

  return {
    name: 'internal-analytics',
    config: { endpoint },
    initialize: () => {
      eventBuffer.start();
    },
    page: ({ payload }) => {
      const properties = payload.properties ?? {};
      const event = mapProperties('PAGE_VIEW', properties);
      event.pageName = properties.path ?? properties.pageName;
      eventBuffer.enqueue(event);
    },
    track: ({ payload }) => {
      const { event: eventName, properties = {} } = payload;
      eventBuffer.enqueue(mapProperties(eventName, properties));
    },
    loaded: () => true,
  };
}
